import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { GtpWalletInfoResult, useReactNativeProps } from '@eika-infrastruktur/mobilbank-lib';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { CreditCardWalletType, WalletCreditCard, WalletCreditCardFilter } from '@generated-types';

import { getEligibleCreditCards } from './getEligibleCreditCards';

export const useEligibleCreditCardsQueryForApple = <T = WalletCreditCard[]>(
    options?: Omit<UseQueryOptions<WalletCreditCard[], RequestExceptionWithResponse, T>, 'queryKey' | 'queryFn'>,
) => {
    const nativeProps = useReactNativeProps();

    const params: WalletCreditCardFilter = {
        isWatchPaired: nativeProps.appleWallet?.isWatchPaired ?? false,
        walletType: CreditCardWalletType.APPLE_PAY,
        panReferenceIds: nativeProps.appleWallet?.localPasses.map((pass) => pass.value) ?? [],
        remotePanReferenceIds: nativeProps.appleWallet?.remotePasses?.map((pass) => pass.value) ?? [],
    };

    return useQuery({
        queryKey: ['v4', 'wallet', 'credit-cards', 'eligible', params],
        queryFn: () => getEligibleCreditCards(params),
        ...options,
    });
};

export const useEligibleCreditCardsQueryForGoogle = <T = WalletCreditCard[]>(
    googleWalletInfo: GtpWalletInfoResult,
    options?: Omit<UseQueryOptions<WalletCreditCard[], RequestExceptionWithResponse, T>, 'queryKey' | 'queryFn'>,
) => {
    const nativeProps = useReactNativeProps();

    const params: WalletCreditCardFilter = {
        walletType: CreditCardWalletType.GOOGLE_PAY,
        deviceId: googleWalletInfo.deviceStableHardwareId,
        isWatchPaired: nativeProps.appleWallet?.isWatchPaired ?? false,
    };

    return useQuery({
        queryKey: ['v4', 'wallet', 'credit-cards', 'eligible', params],
        queryFn: () => getEligibleCreditCards(params),
        ...options,
    });
};
